// ServiceNow integration client (/api/servicenow).
//
// Mirrors backend/apis/servicenow_router.py. health() and discover() are diagnostics
// and are the first things to call when a sync misbehaves — health() is deliberately
// callable while the integration is switched off, and reports why.

import { get, post, AI_TIMEOUT_MS } from "./http";

/** Reachability + auth + per-table access. Never throws for a *ServiceNow* problem —
 *  the bad news is in the body (reachable:false, error, status). */
export async function health() {
  return get("/servicenow/health", { timeout: AI_TIMEOUT_MS });
}

export async function config() {
  return get("/servicenow/config");
}

/** Probe the tenant for usable tables and real state choice values.
 *  Returns a `recommended_env` block to paste into backend/.env. */
export async function discover() {
  return get("/servicenow/discover", { timeout: AI_TIMEOUT_MS });
}

/** Deep links + last-sync state for one demand. Safe with the integration off. */
export async function demandLink(demandId) {
  return get(`/servicenow/demands/${encodeURIComponent(demandId)}/link`);
}

/** Push this demand to ServiceNow (create or update — idempotent on correlation_id). */
export async function pushDemand(demandId, actor = "System") {
  return post(`/servicenow/demands/${encodeURIComponent(demandId)}/push`, { actor },
    { timeout: AI_TIMEOUT_MS });
}

/** What a pull would import, without writing anything. */
export async function previewPull({ limit = 25, query } = {}) {
  return get("/servicenow/pull/preview", { query: { limit, query }, timeout: AI_TIMEOUT_MS });
}

/** Import ServiceNow demands. Screening results on existing rows are never overwritten. */
export async function pull({ limit, query, actor = "System", dryRun = false } = {}) {
  return post("/servicenow/pull", { limit, query, actor, dry_run: dryRun },
    { timeout: AI_TIMEOUT_MS });
}

export async function syncLog({ demandId, limit = 50 } = {}) {
  return get("/servicenow/sync-log", { query: { demand_id: demandId, limit } });
}

/**
 * Every ServiceNow record AIEA has created, joined to the demand each came from.
 *
 * Live state (state, priority, updated) is read from ServiceNow at request time,
 * so a ticket someone has worked or closed shows its real status rather than what
 * AIEA recorded at push time. If the tenant is unreachable the stored links still
 * come back with `warning` set and `liveStateAvailable: false` per row.
 */
export async function tickets({ limit = 100 } = {}) {
  return get("/servicenow/tickets", { query: { limit }, timeout: AI_TIMEOUT_MS });
}
